/**
 * StudySync Notification Popup Component
 * Displays action feedback as overlay popup with close button
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box
} from '@mui/material';
import { Close, CheckCircle, Error, Info, Warning } from '@mui/icons-material';

export interface NotificationData {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  message: string;
  timestamp: Date;
  action?: string;
}

interface NotificationPopupProps {
  notification: NotificationData | null;
  onClose: () => void;
}

const NotificationPopup: React.FC<NotificationPopupProps> = ({ notification, onClose }) => {
  if (!notification) return null;

  const getIcon = () => {
    switch (notification.type) {
      case 'success':
        return <CheckCircle sx={{ color: 'success.main', mr: 1 }} />;
      case 'error':
        return <Error sx={{ color: 'error.main', mr: 1 }} />;
      case 'warning':
        return <Warning sx={{ color: 'warning.main', mr: 1 }} />;
      default:
        return <Info sx={{ color: 'info.main', mr: 1 }} />;
    }
  };


  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        zIndex: 9999,
        '& .MuiDialog-paper': {
          borderRadius: 3,
          boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
          border: '1px solid rgba(0,0,0,0.05)',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {getIcon()}
          <Typography variant="h6" component="span">
            {notification.title}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: 'grey.500' }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2, pb: 1 }}>
        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
          {notification.message}
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={onClose} 
          variant="contained" 
          color="primary"
          fullWidth
          sx={{
            borderRadius: 2,
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            textTransform: 'none',
          }}
        >
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationPopup;
